import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import classNames from "utils/class-names";
import { FILTER_TYPE } from "utils/constants";
import { AiFillBell, AiFillFileText, AiOutlineFileExclamation } from "react-icons/ai";

const convertTimestampToDate = (timestamp) => {
  if (isNaN(timestamp)) {
    return "Invalid Date";
  }

  const date = new Date(timestamp * 1000);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");

  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
};

export const DOCUMENT_COLUMNS_DATA = ({
  openObservationDetails,
  downloadInvestigationDocument,
  openPatientDetails,
  openObservationValue,
  openSampleCollection,
  filters,
  fIsSuperFranchise,
  subFranchise,
  fIsFranchiseEmployee,
  toggleSelectAll,
  selectedRows,
  editableRows,
  setEditableRows,
  toggleSelectRow,
  data,
  setCurrentRowData,
  openDownloadPdf,
  openDownloadPdfSuccessFully,
  directDownloadPdfSuccessFully,
  openHtml,
  handleUploadAttachmentWithObservation,
  documentStatusMap,
  openPatientDocument,
}) => {
  // const processedData = useMemo(() => {
  //   const seenPatientEntries = new Set();

  //   return data.map((row) => {
  //     const patientKey = `${row.patient_name}-${row.appointment_id}`;

  //     if (seenPatientEntries.has(patientKey)) {
  //       return { ...row, patient_name_display: "", age_gender_display: "", company_name_display: "" };
  //     }

  //     seenPatientEntries.add(patientKey);

  //     const ageGender = row.patient_age
  //       ? `${row.patient_age.year}Y ${row.patient_age.month}M ${row.patient_age.days}D | ${row.gender} | ${row.refer_doctor_name}`
  //       : "-";

  //     return {
  //       ...row,
  //       patient_name_display: row.patient_name,
  //       age_gender_display: ageGender,
  //       company_name_display: row.franchise_code || "",
  //     };
  //   });
  // }, [data]);

  const seenPatientEntries = new Set();
  const processedData = data.map((row) => {
    const patientKey = `${row.patient_name}-${row.appointment_id}`;

    if (seenPatientEntries.has(patientKey)) {
      return {
        ...row,
        patient_name_display: "",
        age_gender_display: "",
        company_name_display: "",
        refer_doctor_name_display: "",
      };
    }

    seenPatientEntries.add(patientKey);

    const ageGender = row.patient_age
      ? `${row.patient_age.year}Y ${row.patient_age.month}M ${row.patient_age.days}D | ${row.gender}`
      : "-";

    return {
      ...row,
      patient_name_display: row.patient_name,
      age_gender_display: ageGender,
      refer_doctor_name_display: row.refer_doctor_name || "",
      company_name_display: row.franchise_code || "",
    };
  });

  return [
    {
      Header: "S.No",
      accessor: "_index",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isUrgent = row.original.investigation_data?.urgent;
        return (
          <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            {row.index + 1}
            {isUrgent && <AiFillBell color="red" />}
          </span>
        );
      },
    },
    {
      Header: "Registration Details",
      accessor: "registration_details",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { generated, appointment_id } = row.original;
        return (
          <span>
            {appointment_id && (
              <>
                {appointment_id} <br />
              </>
            )}
            {generated && <>{convertTimestampToDate(generated)}</>}
          </span>
        );
      },
    },
    {
      Header: "Patient Name",
      accessor: "patient_name",
      disableSortBy: true,
      Cell: ({ row }) => {
        // return <span>{row?.original?.patient_name || "-"}</span>;
        const patientName = processedData[row.index]?.patient_name_display;
        if (!patientName) return null;

        return (
          <span
            style={{
              wordWrap: "break-word",
              whiteSpace: "normal",
              display: "block",
            }}
          >
            {patientName}
          </span>
        );
      },
    },
    {
      Header: "Age, Gender & Doctor Name",
      accessor: "age_gender",
      disableSortBy: true,
      Cell: ({ row }) => {
        const ageGender = processedData[row.index]?.age_gender_display;
        const doctorName = processedData[row.index]?.refer_doctor_name_display;

        if (!ageGender && !doctorName) return null;

        return (
          <span
            style={{ textTransform: "capitalize", display: "inline-block" }}
          >
            {ageGender}
            {doctorName && (
              <>
                <br />
                {doctorName}
              </>
            )}
          </span>
        );
      },
    },
    {
      Header: "Panel",
      accessor: "company_name",
      disableSortBy: true,
      Cell: ({ row }) => {
        const companyName = processedData[row.index]?.company_name_display;
        if (!companyName) return null;

        return (
          <span
            style={{
              display: "inline-block",
              maxWidth: "150px",
              wordWrap: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.2",
            }}
          >
            {companyName}
          </span>
        );
      },
    },
    {
      Header: "Investigation & Barcode",
      accessor: "investigation_barcode",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { investigation_data } = row.original;

        const { package_obj } = investigation_data;

        const handleClick = () => {
          setCurrentRowData(row?.original);
        };

        return (
          <span>
            <a
              onClick={(e) => {
                e.preventDefault();
                if (
                  subFranchise === 1 ||
                  fIsSuperFranchise === 1 ||
                  fIsFranchiseEmployee === 1
                ) {
                  handleClick();
                } else {
                  if (
                    row.original.investigation_data.status !== "PENDING" &&
                    row.original.investigation_data.status !== "COLLECTED"
                  ) {
                    openObservationValue(row.original);
                  } else {
                    openSampleCollection(row.original);
                  }
                }
              }}
              style={{
                fontWeight: "700",
                textDecoration: "underline",
                cursor: "pointer",
                display: "inline-block",
                maxWidth: "150px",
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {package_obj?.package_name || ""} <br />
              {investigation_data?.investigation_name || ""} <br />
              {investigation_data?.barcode_no || ""}
            </a>
          </span>
        );
      },
    },
    {
      Header: (
        <input
          type="checkbox"
          onChange={(e) => toggleSelectAll(e.target.checked)}
          checked={
            selectedRows?.length > 0 &&
            selectedRows?.length ===
              data?.filter(
                (row) =>
                  row.investigation_data?.status === "RESULT_DONE" ||
                  row.investigation_data?.status === "APPROVED" ||
                  row.investigation_data?.status === "REPORT_ISSUED"
              ).length &&
            data?.filter(
              (row) =>
                row.investigation_data?.status === "RESULT_DONE" ||
                row.investigation_data?.status === "APPROVED" ||
                row.investigation_data?.status === "REPORT_ISSUED"
            ).length > 0
          }
          indeterminate={
            selectedRows?.length > 0 &&
            selectedRows?.length <
              data?.filter(
                (row) =>
                  row.investigation_data?.status === "RESULT_DONE" ||
                  row.investigation_data?.status === "APPROVED" ||
                  row.investigation_data?.status === "REPORT_ISSUED"
              ).length
              ? true
              : undefined
          }
        />
      ),
      accessor: "_select",
      disableSortBy: true,
      Cell: ({ row }) => {
        const rowIndex = row?.index;
        const isChecked = selectedRows.includes(rowIndex);
        const { investigation_data } = row.original;
        const status = investigation_data?.status;

        return (
          (status === "RESULT_DONE" ||
            status === "APPROVED" ||
            status === "REPORT_ISSUED") && (
            <input
              type="checkbox"
              onChange={() => toggleSelectRow(rowIndex, row.original)}
              checked={isChecked}
            />
          )
        );
      },
    },
    {
      Header: "",
      accessor: "ids",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <>
            <button
              disabled={!filters.is_active}
              className={classNames("btn dropdown-toggle", {
                "btn-primary": filters.is_active,
                "btn-secondary": !filters.is_active,
              })}
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              More...
            </button>
            <ul className="dropdown-menu">
              {/* {fIsSuperFranchise !== 1 &&
                fIsFranchiseEmployee !== 1 &&
                subFranchise !== 1 && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        openObservationValue(cell.row.original);
                      }}
                    >
                      Add Observation Value
                    </button>
                  </li>
                )} */}
              {/* {["APPROVED", "REPORT ISSUED", "RESULTDONE"].includes(
                filters?.app_status
              ) && ( */}
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => openObservationDetails(cell.row.original)}
                >
                  View
                </button>
              </li>
              <li>
                <Link
                  to="/patient-document"
                  state={{ patientData: cell.row.original }}
                  className="dropdown-item"
                >
                  Show Patient Document
                </Link>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() =>
                    handleUploadAttachmentWithObservation(cell.row.original)
                  }
                >
                  Upload Attachment
                </button>
              </li>
              {/* <li>
                <button
                  className="dropdown-item"
                  onClick={() => downloadInvestigationDocument(cell.row.original)}
                >
                  Investigation Document
                </button>
              </li> */}
              {/* )} */}
              {/* {filters?.app_status !== "PENDING" && ( */}
              {/* <li>
                <Link
                  to={`/ResultEntryPdf?display_header=true&display_footer_banner=true&display_banner=true&display_footer=true&display_address=true&display_watermark=true&investigation_ids=${cell?.row?.original?.investigation_data?.investigation_id}&appointment_id=${cell?.row?.original?.appointment_id}`}
                  className="acc-link"
                >
                  <button className="dropdown-item">Show Generate PDF</button>
                </Link>
              </li> */}
              {/* )} */}
              {/* <li>
                <button
                  className="dropdown-item"
                  onClick={() => openDownloadPdf(cell.row.original)}
                >
                  Download PDF
                </button>
              </li> */}
              {/* <li>
                <button
                  className="dropdown-item"
                  onClick={() => openDownloadPdfSuccessFully(cell.row.original)}
                >
                  Generate PDF
                </button>
              </li> */}
              {/* <li>
                <Link
                  to={`/ResultEntryReactPdf?display_header=true&display_footer=true&display_address=true&display_watermark=true&investigation_id=${cell?.row?.original?.investigation_data?.investigation_id}&appointment_id=${cell?.row?.original?.appointment_id}`}
                  className="acc-link"
                >
                  <button className="dropdown-item">Generate React PDF</button>
                </Link>
              </li> */}
            </ul>
          </>
        );
      },
    },
    {
      Header: "Document",
      accessor: "_document",
      disableSortBy: true,
      Cell: ({ row }) => {
        const original = row.original;
        const key = `${original?.appointment_id}_${original?.investigation_data?.investigation_id}`;
        const status = documentStatusMap?.[key];

        if (!status || status.loading) {
          return <span className="spinner-border spinner-border-sm" role="status" />;
        }
        return (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => openPatientDocument(original)}
            title={status.hasDocument ? "Document available" : "No document"}
          >
            {status.hasDocument ? (
              <AiFillFileText color="green" size={20} />
            ) : (
              <AiOutlineFileExclamation color="red" size={20} />
            )}
          </span>
        );
      },
    },
  ];
};

export const ADD_OBSERVATION_COLUMN_DATA = () => {
  return [
    {
      Header: "Observation Name",
      accessor: "observation_name",
      disableSortBy: true,
    },
    {
      Header: "Investigation",
      accessor: "investigation_details.investigation_name",
      disableSortBy: true,
    },
    {
      Header: "Patient Name",
      accessor: "patient_details.patient_name",
      disableSortBy: true,
    },
    {
      Header: "MacReading",
      accessor: "max_critical",
      disableSortBy: true,
    },
  ];
};

function getIsInvestigationChecked(checkedSource, investigationKey) {
  if (!checkedSource) {
    return true;
  }
  const map =
    checkedSource.current !== undefined
      ? checkedSource.current
      : checkedSource;
  if (!map || typeof map !== "object") {
    return true;
  }
  return map[investigationKey] === true;
}

export function observationValuesEquivalent(storedValue, displayValue) {
  if (storedValue === displayValue) {
    return true;
  }
  if (
    storedValue === undefined ||
    storedValue === null ||
    storedValue === "" ||
    displayValue === undefined ||
    displayValue === null ||
    displayValue === ""
  ) {
    return false;
  }
  const storedNum = Number(storedValue);
  const displayNum = Number(displayValue);
  if (Number.isFinite(storedNum) && Number.isFinite(displayNum)) {
    return storedNum === displayNum;
  }
  return String(storedValue).trim() === String(displayValue).trim();
}

function formatObservationValueOnBlur(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return Number(numeric).toFixed(2);
  }
  return String(value);
}

function observationValueToInputString(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  return String(value);
}

function getObservationNavigationInputs() {
  if (typeof document === "undefined") {
    return [];
  }
  return Array.from(
    document.querySelectorAll(
      '[data-observation-input="true"]:not([disabled]):not([readonly])'
    )
  );
}

function focusNextObservationInput(currentTarget, direction = 1) {
  const inputs = getObservationNavigationInputs();
  if (!inputs.length) {
    return false;
  }

  const currentIndex = inputs.findIndex((el) => el === currentTarget);
  const normalizedDirection = direction < 0 ? -1 : 1;

  if (currentIndex === -1) {
    const fallbackIndex = normalizedDirection > 0 ? 0 : inputs.length - 1;
    inputs[fallbackIndex]?.focus();
    return true;
  }

  const nextIndex =
    (currentIndex + normalizedDirection + inputs.length) % inputs.length;
  inputs[nextIndex]?.focus();
  return true;
}

export function isTableObservationInput(inputRefs, element) {
  if (!element) {
    return false;
  }
  if (
    element instanceof HTMLElement &&
    element.closest('[data-observation-input="true"]')
  ) {
    return true;
  }
  return (
    inputRefs?.current?.some((el) => el && el === element)
  );
}

function getObservationInputInitialValue(row, pendingObservationValuesRef) {
  const obsId = String(row?.original?.observation_id ?? "");
  const pending = pendingObservationValuesRef?.current?.get(obsId);
  if (pending && (pending.value !== undefined && pending.value !== null)) {
    return observationValueToInputString(pending.value);
  }
  return observationValueToInputString(row?.original?.observation_value);
}

const ObservationValueCell = React.memo(function ObservationValueCell({
  row,
  handleValueChange,
  inputRefs,
  numericValueAcceptableObservations,
  calculatedObservationIds,
  manualOverrideObservationIdsRef,
  unlockedCalculatedObservationIds,
  handleEnableCalculatedObservationEdit,
  handleResetCalculatedObservation,
  checkedInvestigations,
  lastFocusedInput,
  editingObservationIdRef,
  onObservationEditEnd,
  macInitializedKeysRef,
  pendingObservationValuesRef,
  isNavigatingWithinTableRef,
  calculationMode = "auto",
  handleCalculatedObservationFocus = null,
  readOnlyStatuses = ["APPROVED"],
}) {
  const isHeader = row.original.header === true;
  const isOnFocusCalculationMode = calculationMode === "onFocus";
  const isApproved = readOnlyStatuses.includes(row.original.status);
  const cellInputRef = React.useRef(null);
  const [localValue, setLocalValue] = React.useState(() =>
    getObservationInputInitialValue(
      row,
      isOnFocusCalculationMode ? null : pendingObservationValuesRef
    )
  );
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const helpIconRef = React.useRef(null);
  const formulaSourceDebounceRef = React.useRef(null);

  const investigationKey = `${row.original.investigation_details.lab_department.department}-${row.original.investigation_details.investigation_name}`;

  const isInvestigationChecked = getIsInvestigationChecked(
    checkedInvestigations,
    investigationKey
  );

  const filteredHelp = row.original.observation_help
    ? row.original.observation_help.filter(
        (help) => help.observation_id === row.original.observation_id
      )
    : [];

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (helpIconRef.current && helpIconRef.current.contains(event.target)) {
        return;
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        showHelp
      ) {
        setShowHelp(false);
      }
    }

    if (showHelp) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelp]);

  React.useEffect(() => {
    if (isOnFocusCalculationMode) {
      return;
    }

    const initialValue = row?.original?.observation_value;
    const status = row?.original?.status;
    const machineReading = row?.original?.machine_reading;
    const valueToApply =
      initialValue !== undefined &&
      initialValue !== null &&
      String(initialValue) !== ""
        ? initialValue
        : machineReading;

    const obsId = row?.original?.observation_id;
    const department =
      row?.original?.investigation_details?.lab_department?.department;
    const investigation =
      row?.original?.investigation_details?.investigation_name;
    const appointmentId = row?.original?.appointment_id ?? "";
    const macKey = `${appointmentId}-${obsId}`;

    if (macInitializedKeysRef?.current?.has(macKey)) {
      return;
    }
    if (!macInitializedKeysRef?.current && isInitialized) {
      return;
    }

    if (
      valueToApply !== undefined &&
      valueToApply !== null &&
      String(valueToApply) !== "" &&
      status === "MAC_DATA" &&
      machineReading !== null &&
      obsId &&
      department &&
      investigation
    ) {
      const timeoutId = setTimeout(() => {
        if (isTableObservationInput(inputRefs, document.activeElement)) {
          return;
        }
        setLocalValue(observationValueToInputString(valueToApply));
        if (cellInputRef.current) {
          cellInputRef.current.value = observationValueToInputString(
            valueToApply
          );
        }
        handleValueChange(valueToApply, department, investigation, obsId);
        if (macInitializedKeysRef?.current) {
          macInitializedKeysRef.current.add(macKey);
        }
        if (!macInitializedKeysRef?.current) {
          setIsInitialized(true);
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [
    row,
    isInitialized,
    handleValueChange,
    macInitializedKeysRef,
    inputRefs,
    isOnFocusCalculationMode,
  ]);

  React.useEffect(() => {
    if (isOnFocusCalculationMode) {
      if (document.activeElement === cellInputRef.current) {
        return;
      }
      const nextValue = observationValueToInputString(
        row.original.observation_value
      );
      if (nextValue !== localValue) {
        setLocalValue(nextValue);
        if (cellInputRef.current) {
          cellInputRef.current.value = nextValue;
        }
      }
      return;
    }

    const obsId = row.original.observation_id;
    if (
      editingObservationIdRef?.current != null &&
      +editingObservationIdRef.current === +obsId
    ) {
      return;
    }
    const pendingEntry = pendingObservationValuesRef?.current?.get(String(obsId));
    if (pendingEntry) {
      if (!pendingEntry.flushed) {
        if (
          document.activeElement !== cellInputRef.current &&
          !observationValuesEquivalent(localValue, pendingEntry.value)
        ) {
          const nextValue = observationValueToInputString(
            pendingEntry.value
          );
          setLocalValue(nextValue);
          if (cellInputRef.current) {
            cellInputRef.current.value = nextValue;
          }
        }
        return;
      }
      if (
        !observationValuesEquivalent(
          row.original.observation_value,
          pendingEntry.value
        )
      ) {
        if (observationValuesEquivalent(localValue, pendingEntry.value)) {
          return;
        }
      } else {
        pendingObservationValuesRef.current.delete(String(obsId));
      }
    }
    if (observationValuesEquivalent(row.original.observation_value, localValue)) {
      return;
    }
    if (document.activeElement === cellInputRef.current) {
      return;
    }
    const nextValue = observationValueToInputString(
      row.original.observation_value
    );
    setLocalValue(nextValue);
    if (cellInputRef.current) {
      cellInputRef.current.value = nextValue;
    }
  }, [
    row.original.observation_value,
    row.original.observation_id,
    localValue,
    editingObservationIdRef,
    pendingObservationValuesRef,
    isOnFocusCalculationMode,
  ]);

  React.useEffect(() => {
    return () => {
      if (formulaSourceDebounceRef.current) {
        clearTimeout(formulaSourceDebounceRef.current);
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    const acceptableKeys = ["Tab", "Enter"];
    if (!acceptableKeys.includes(e.key)) return;

    if (e.key === "Enter" && filteredHelp.length > 0) {
      e.preventDefault();
      lastFocusedInput.current = e.target;
      setShowHelp(!showHelp);
      return;
    }

    if (e.key === "Tab" || (e.key === "Enter" && filteredHelp.length === 0)) {
      e.preventDefault();
      const direction = e.key === "Tab" && e.shiftKey ? -1 : 1;

      if (isNavigatingWithinTableRef) {
        isNavigatingWithinTableRef.current = true;
      }
      focusNextObservationInput(e.target, direction);
      requestAnimationFrame(() => {
        if (isNavigatingWithinTableRef) {
          isNavigatingWithinTableRef.current = false;
        }
      });
    }
  };

  const minValue = row.original.min_reading;
  const maxValue = row.original.max_reading;

  const isMinMaxValid =
    minValue !== "" &&
    maxValue !== "" &&
    !(Number(minValue) === 0 && Number(maxValue) === 0);

  const isOutOfRange =
    localValue !== "" &&
    isMinMaxValid &&
    (Number(localValue) < Number(minValue) ||
      Number(localValue) > Number(maxValue));

  const isFormulaSourceField = numericValueAcceptableObservations.includes(
    row.original.observation_id
  );
  const departmentName =
    row.original.investigation_details.lab_department.department;
  const investigationName =
    row.original.investigation_details.investigation_name;
  const observationId = row.original.observation_id;

  const handleChange = (e) => {
    const value = e?.target?.value ?? "";
    setLocalValue(value);

    if (isOnFocusCalculationMode) {
      return;
    }

    if (pendingObservationValuesRef?.current) {
      if (value === "" && value !== 0) {
        pendingObservationValuesRef.current.delete(String(observationId));
      } else {
        pendingObservationValuesRef.current.set(String(observationId), {
          value,
          department: departmentName,
          investigation: investigationName,
          flushed: false,
        });
      }
    }

    if (isCalculatedField) {
      handleValueChange(
        value,
        departmentName,
        investigationName,
        observationId,
        true
      );
      return;
    }

    if (isFormulaSourceField) {
      if (formulaSourceDebounceRef.current) {
        clearTimeout(formulaSourceDebounceRef.current);
      }
      formulaSourceDebounceRef.current = setTimeout(() => {
        handleValueChange(
          value,
          departmentName,
          investigationName,
          observationId,
          true
        );
      }, 500);
    }
  };

  const isCalculatedField = calculatedObservationIds.includes(
    row.original.observation_id
  );
  const isNumericField = isFormulaSourceField || isCalculatedField;
  const manualOverrideObservationIds =
    manualOverrideObservationIdsRef?.current || [];
  const isManualOverrideActive = manualOverrideObservationIds.includes(
    row.original.observation_id
  );
  const isCalculatedFieldEditable =
    isOnFocusCalculationMode
      ? true
      : isCalculatedField &&
        (isManualOverrideActive ||
          unlockedCalculatedObservationIds.includes(row.original.observation_id));

  const helpIconStyle = {
    backgroundColor: "#3b82f6",
    color: "white",
    borderRadius: "50%",
    width: "16px",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
    cursor: "pointer",
  };

  if (!isInvestigationChecked) {
    return <span></span>;
  }

  if (isApproved) {
    return (
      <div className="flex items-center">
        <span>{localValue}</span>
      </div>
    );
  }

  if (isHeader) {
    return <span></span>;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          gap: "5px",
        }}
      >
        <input
          ref={(el) => {
            cellInputRef.current = el;
          }}
          type="text"
          inputMode={isNumericField ? "decimal" : "text"}
          defaultValue={localValue}
          data-observation-input="true"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={(e) => {
            if (lastFocusedInput) {
              lastFocusedInput.current = e.target;
            }
            if (isOnFocusCalculationMode && isCalculatedField) {
              const calculatedValue =
                handleCalculatedObservationFocus?.(row.original);
              if (
                calculatedValue !== undefined &&
                calculatedValue !== null &&
                String(calculatedValue) !== ""
              ) {
                const nextValue =
                  observationValueToInputString(calculatedValue);
                setLocalValue(nextValue);
                if (cellInputRef.current) {
                  cellInputRef.current.value = nextValue;
                }
              }
            }
            if (
              !isOnFocusCalculationMode &&
              (!isCalculatedField || isCalculatedFieldEditable) &&
              editingObservationIdRef &&
              row.original.observation_id
            ) {
              editingObservationIdRef.current = row.original.observation_id;
            }
          }}
          onBlur={(e) => {
            if (formulaSourceDebounceRef.current) {
              clearTimeout(formulaSourceDebounceRef.current);
              formulaSourceDebounceRef.current = null;
            }

            let valueToFlush = localValue;
            if (isNumericField && localValue !== "") {
              const formatted = formatObservationValueOnBlur(localValue);
              if (formatted !== localValue) {
                setLocalValue(formatted);
                valueToFlush = formatted;
                if (cellInputRef.current) {
                  cellInputRef.current.value = formatted;
                }
              }
            }

            const movingWithinTable =
              isNavigatingWithinTableRef?.current ||
              isTableObservationInput(inputRefs, e.relatedTarget);

            if (isOnFocusCalculationMode) {
              handleValueChange(
                valueToFlush,
                departmentName,
                investigationName,
                observationId
              );
              return;
            }

            if (isCalculatedField && !isCalculatedFieldEditable) {
              if (editingObservationIdRef) {
                editingObservationIdRef.current = null;
              }
              return;
            }

            handleValueChange(
              valueToFlush,
              departmentName,
              investigationName,
              observationId,
              true,
              !movingWithinTable
            );

            if (!movingWithinTable && editingObservationIdRef) {
              if (+editingObservationIdRef.current === +observationId) {
                editingObservationIdRef.current = null;
              }
              if (onObservationEditEnd) {
                onObservationEditEnd();
              }
            }
          }}
          style={{
            backgroundColor: isOutOfRange ? "#f76060" : "white",
            color: isOutOfRange ? "white" : "black",
            border: isOutOfRange
              ? "1px solid #f76060"
              : "1px solid #d9d9d9",
            padding: "5px",
            borderRadius: "4px",
            outline: "none",
            cursor:
              isCalculatedField && !isCalculatedFieldEditable
                ? "not-allowed"
                : "text",
          }}
          readOnly={isCalculatedField && !isCalculatedFieldEditable}
        />
        {!isOnFocusCalculationMode && isCalculatedField && (
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              fontSize: "11px",
            }}
          >
            {!isCalculatedFieldEditable ? (
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => handleEnableCalculatedObservationEdit?.(row.original)}
                style={{ fontSize: "11px", textDecoration: "underline" }}
              >
                Edit
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => handleResetCalculatedObservation?.(row.original)}
                style={{ fontSize: "11px", textDecoration: "underline" }}
              >
                Reset Auto
              </button>
            )}
            {isManualOverrideActive && (
              <span style={{ color: "#d97706", fontWeight: 600 }}>
                Manual Override
              </span>
            )}
          </div>
        )}

        {showHelp && (
          <select
            size={filteredHelp?.length}
            ref={dropdownRef}
            className="absolute z-10 bg-white border border-gray-200 rounded shadow-lg p-2 w-64"
            style={{
              top: "100%",
              left: "0",
              overflowY: "auto",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const selectedItem = filteredHelp.find(
                  (item) => item.id.toString() === e.target.value
                );
                if (selectedItem) {
                  const valueToSet =
                    selectedItem.help_value || selectedItem.help_header;
                  setLocalValue(valueToSet);
                  if (cellInputRef.current) {
                    cellInputRef.current.value = valueToSet;
                  }
                  setShowHelp(false);

                  handleValueChange(
                    valueToSet,
                    row.original.investigation_details.lab_department.department,
                    row.original.investigation_details.investigation_name,
                    row.original.observation_id,
                    false
                  );
                }

                setTimeout(() => {
                  lastFocusedInput.current?.focus();
                }, 0);
              }
            }}
            onClick={(e) => {
              const selectedItem = filteredHelp.find(
                (item) => item.id.toString() === e.target.value
              );
              if (selectedItem) {
                const valueToSet =
                  selectedItem.help_value || selectedItem.help_header;
                setLocalValue(valueToSet);
                if (cellInputRef.current) {
                  cellInputRef.current.value = valueToSet;
                }
                setShowHelp(false);

                handleValueChange(
                  valueToSet,
                  row.original.investigation_details.lab_department.department,
                  row.original.investigation_details.investigation_name,
                  row.original.observation_id
                );
              }
            }}
            onBlur={() => setShowHelp(false)}
            autoFocus
          >
            {filteredHelp.map((item) => (
              <option key={item.id} value={item.id}>
                {item.help_header}
              </option>
            ))}
          </select>
        )}
      </div>

      {filteredHelp.length > 0 && (
        <div className="relative">
          <span
            ref={helpIconRef}
            style={helpIconStyle}
            onClick={(e) => {
              e.stopPropagation();
              setShowHelp(!showHelp);
            }}
          >
            ?
          </span>
        </div>
      )}
    </div>
  );
});

const SimpleObservationValueCell = React.memo(function SimpleObservationValueCell({
  row,
  handleValue,
}) {
  const isHeader = row.original.header === true;
  const cellInputRef = useRef(null);
  const [localValue, setLocalValue] = useState(
    row.original.observation_value || ""
  );
  const [showHelp, setShowHelp] = useState(false);
  const dropdownRef = useRef(null);
  const helpIconRef = useRef(null);

  const filteredHelp = row.original.observation_help
    ? row.original.observation_help.filter(
        (help) => help.observation_id === row.original.observation_id
      )
    : [];

  useEffect(() => {
    if (
      row.original.observation_value !== localValue &&
      document.activeElement !== cellInputRef.current
    ) {
      setLocalValue(row.original.observation_value || "");
    }
  }, [row.original.observation_value, localValue, row.original]);

  useEffect(() => {
    row.original.observation_value = localValue;
  }, [localValue, row.original]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (helpIconRef.current && helpIconRef.current.contains(event.target)) {
        return;
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        showHelp
      ) {
        setShowHelp(false);
      }
    }

    if (showHelp) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelp]);

  const handleKeyDown = (e) => {
    const acceptableKeys = ["Tab", "Enter"];
    if (!acceptableKeys.includes(e.key)) return;

    e.preventDefault();
    const direction = e.key === "Tab" && e.shiftKey ? -1 : 1;
    focusNextObservationInput(e.target, direction);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    row.original.observation_value = newValue;
  };

  const helpIconStyle = {
    backgroundColor: "#3b82f6",
    color: "white",
    borderRadius: "50%",
    width: "16px",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
    cursor: "pointer",
  };

  if (isHeader) {
    return <span></span>;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          gap: "5px",
        }}
      >
        <input
          ref={(el) => {
            cellInputRef.current = el;
          }}
          type="text"
          defaultValue={localValue}
          data-observation-input="true"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            handleValue(
              row.original.observation_id,
              localValue,
              row.original.investigation_id
            );
          }}
          style={{
            backgroundColor: "white",
            color: "black",
            border: "1px solid #d9d9d9",
            padding: "5px",
            borderRadius: "4px",
            outline: "none",
          }}
        />
        {showHelp && (
          <div
            ref={dropdownRef}
            className="absolute z-10 bg-white border border-gray-200 rounded shadow-lg p-2 w-64"
            style={{
              top: "100%",
              left: "0",
            }}
          >
            {filteredHelp.map((item) => (
              <div
                key={item.id}
                className="hover:bg-blue-100 cursor-pointer rounded"
                onClick={() => {
                  const valueToSet = item.help_value || item.help_header;
                  setLocalValue(valueToSet);
                  if (cellInputRef.current) {
                    cellInputRef.current.value = valueToSet;
                  }
                  setShowHelp(false);
                  row.original.observation_value = valueToSet;
                  handleValue(
                    row.original.observation_id,
                    valueToSet,
                    row.original.investigation_id
                  );
                }}
              >
                <p className="text-sm text-gray-700">{item.help_header}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {filteredHelp.length > 0 && (
        <div className="relative">
          <span
            ref={helpIconRef}
            style={helpIconStyle}
            onClick={(e) => {
              e.stopPropagation();
              setShowHelp(!showHelp);
            }}
          >
            ?
          </span>
        </div>
      )}
    </div>
  );
});

const ObservationFlagCell = React.memo(function ObservationFlagCell({
  row,
  handleObservationMetaFieldChange = null,
  readOnlyStatuses = ["APPROVED"],
}) {
  const isHeader = row.original.header === true;
  const isApproved = readOnlyStatuses.includes(row.original.status);
  const observationValue = Number(row.original.observation_value);
  const minValue =
    row?.original?.min_reading !== undefined &&
    row?.original?.min_reading !== ""
      ? Number(row.original.min_reading)
      : null;

  const maxValue =
    row?.original?.max_reading !== undefined &&
    row?.original?.max_reading !== ""
      ? Number(row.original.max_reading)
      : null;

  const getFlag = React.useCallback(() => {
    if (
      minValue === null ||
      maxValue === null ||
      (minValue === 0 && maxValue === 0)
    ) {
      return "Normal";
    }

    if (observationValue > maxValue) return "High";
    if (observationValue < minValue) return "Low";
    return "Normal";
  }, [maxValue, minValue, observationValue]);

  const [selectedFlag, setSelectedFlag] = React.useState(
    row.original.flag || getFlag()
  );

  React.useEffect(() => {
    const autoFlag = getFlag();
    setSelectedFlag(autoFlag);
    if (handleObservationMetaFieldChange) {
      handleObservationMetaFieldChange(
        row.original.investigation_details.lab_department.department,
        row.original.investigation_details.investigation_name,
        row.original.observation_id,
        "flag",
        autoFlag
      );
      return;
    }
    row.original.flag = autoFlag;
  }, [getFlag, row, handleObservationMetaFieldChange]);

  const handleFlagChange = (e) => {
    const newFlag = e.target.value;
    setSelectedFlag(newFlag);
    if (handleObservationMetaFieldChange) {
      handleObservationMetaFieldChange(
        row.original.investigation_details.lab_department.department,
        row.original.investigation_details.investigation_name,
        row.original.observation_id,
        "flag",
        newFlag
      );
      return;
    }
    row.original.flag = newFlag;
  };

  if (isHeader) {
    return <span></span>;
  }

  return (
    <select value={selectedFlag} onChange={handleFlagChange} disabled={isApproved}>
      <option value="Normal">Normal</option>
      <option value="High">High</option>
      <option value="Low">Low</option>
    </select>
  );
});

const ObservationDisplayReadingCell = React.memo(
  function ObservationDisplayReadingCell({
    row,
    handleObservationMetaFieldChange = null,
    readOnlyStatuses = ["APPROVED"],
  }) {
    const isHeader = row.original.header === true;
    const isApproved = readOnlyStatuses.includes(row.original.status);
    const [displayReading, setDisplayReading] = React.useState(
      row.original.display_reading || ""
    );

    React.useEffect(() => {
      if (row.original.display_reading !== displayReading) {
        setDisplayReading(row.original.display_reading || "");
      }
    }, [row.original.display_reading, displayReading]);

    React.useEffect(() => {
      if (handleObservationMetaFieldChange) {
        return;
      }
      row.original.display_reading = displayReading;
    }, [displayReading, row.original, handleObservationMetaFieldChange]);

    const handleDisplayReadingChange = (e) => {
      const newValue = e.target.value;
      setDisplayReading(newValue);
      if (handleObservationMetaFieldChange) {
        handleObservationMetaFieldChange(
          row.original.investigation_details.lab_department.department,
          row.original.investigation_details.investigation_name,
          row.original.observation_id,
          "display_reading",
          newValue
        );
        return;
      }
      row.original.display_reading = newValue;
    };

    if (isHeader) {
      return <span></span>;
    }

    return (
      <textarea
        style={{ resize: "both", overflow: "auto", width: "100%" }}
        value={displayReading}
        onChange={handleDisplayReadingChange}
        disabled={isApproved}
      />
    );
  }
);

const ObservationMacReadingCell = React.memo(function ObservationMacReadingCell({
  row,
  handleValue,
}) {
  const isHeader = row.original.header === true;
  const cellInputRef = React.useRef(null);
  const [localMacValue, setLocalMacValue] = React.useState(
    row.original.observation_value_mac_reading || ""
  );

  React.useEffect(() => {
    if (
      row.original.observation_value_mac_reading !== localMacValue &&
      document.activeElement !== cellInputRef.current
    ) {
      setLocalMacValue(row.original.observation_value_mac_reading || "");
    }
  }, [row.original.observation_value_mac_reading, localMacValue]);

  const handleKeyDown = (e) => {
    const acceptableKeys = ["Tab", "Enter"];
    if (!acceptableKeys.includes(e.key)) return;

    e.preventDefault();
    const direction = e.key === "Tab" && e.shiftKey ? -1 : 1;
    focusNextObservationInput(e.target, direction);
  };

  const handleMacChange = (e) => {
    const newValue = e.target.value;
    setLocalMacValue(newValue);
    row.original.observation_value_mac_reading = newValue;
  };

  if (isHeader) {
    return <span></span>;
  }

  return (
    <input
      type="text"
      ref={(el) => {
        cellInputRef.current = el;
      }}
      defaultValue={localMacValue}
      data-observation-input="true"
      onChange={handleMacChange}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        handleValue(
          row.original.observation_id,
          localMacValue,
          row.original.investigation_id,
          "observation_value_mac_reading"
        );
      }}
      style={{
        backgroundColor: "white",
        color: "black",
        border: "1px solid #d9d9d9",
        padding: "5px",
        borderRadius: "4px",
        outline: "none",
      }}
    />
  );
});

export const ADD_OBSERVATION_COLUMNS_DATA = (
  handleValueChange,
  inputRefs,
  numericValueAcceptableObservations,
  calculatedObservationIds = [],
  manualOverrideObservationIdsRef = null,
  unlockedCalculatedObservationIds = [],
  handleEnableCalculatedObservationEdit = null,
  handleResetCalculatedObservation = null,
  checkedInvestigations,
  lastFocusedInput,
  allInputRefs,
  totalInputs,
  refMap,
  handleToggleObservationNabl,
  nablUpdatingKeys = [],
  editingObservationIdRef = null,
  onObservationEditEnd = null,
  macInitializedKeysRef = null,
  pendingObservationValuesRef = null,
  isNavigatingWithinTableRef = null,
  calculationMode = "auto",
  handleCalculatedObservationFocus = null,
  readOnlyStatuses = ["APPROVED"],
  handleObservationMetaFieldChange = null
) => {
  const columns = [
    {
      Header: "Observation Name",
      accessor: "observation_name",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isHeader = row.original.header === true;

        return (
          <>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px" }}
            >
              <span
                style={{
                  fontWeight: isHeader ? "bold" : "normal",
                  textTransform: isHeader ? "uppercase" : "none",
                  marginLeft: isHeader ? "-12px" : "0px",
                }}
              >
                {row.original.observation_name}
              </span>
            </div>
          </>
        );
      },
    },
    ...(handleToggleObservationNabl
      ? [
          {
            Header: "isNabl",
            accessor: "is_nabl",
            disableSortBy: true,
            Cell: ({ row }) => {
              const isHeader = row.original.header === true;
              const key = `${row.original?.investigation_id}-${row.original?.observation_id}`;
              if (isHeader) {
                return <span></span>;
              }

              return (
                <input
                  type="checkbox"
                  checked={Boolean(row.original?.is_nabl)}
                  disabled={nablUpdatingKeys.includes(key)}
                  onChange={(e) =>
                    handleToggleObservationNabl(row.original, e.target.checked)
                  }
                />
              );
            },
          },
        ]
      : []),
    // {
    //   Header: "Value",
    //   accessor: "observation_value",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const isHeader = row.original.header === true;
    //     const isApproved = row.original.status === "APPROVED";
    //     const rowIndex = row.index;
    //     const [localValue, setLocalValue] = React.useState(
    //       row.original.observation_value || ""
    //     );

    //     React.useEffect(() => {
    //       if (
    //         row.original.observation_value !== localValue &&
    //         document.activeElement !== inputRefs.current[rowIndex]
    //       ) {
    //         setLocalValue(row.original.observation_value || "");
    //       }
    //     }, [row.original.observation_value]);

    //     const handleKeyDown = (e) => {
    //       const acceptableKeys = ["Tab", "Enter"];
    //       console.log("Pressed Key:", e.key);

    //       if (!acceptableKeys.includes(e.key)) return;

    //       e.preventDefault();
    //       const direction = e.key === "Tab" && e.shiftKey ? -1 : 1;
    //       console.log("Direction:", direction);

    //       const currentIndex = inputRefs.current.findIndex(
    //         (el) => el === document.activeElement
    //       );
    //       console.log("Current Index:", currentIndex);

    //       let nextIndex = currentIndex + direction;
    //       console.log("Initial Next Index:", nextIndex);

    //       while (!inputRefs.current[nextIndex]) {
    //         console.log("Skipping invalid input at index:", nextIndex);
    //         nextIndex += direction;

    //         if (nextIndex >= inputRefs.current.length) {
    //           nextIndex = 0;
    //           console.log("Wrapped to start:", nextIndex);
    //         } else if (nextIndex < 0) {
    //           nextIndex = inputRefs.current.length - 1;
    //           console.log("Wrapped to end:", nextIndex);
    //         }
    //       }

    //       console.log("Focusing on index:", nextIndex);
    //       inputRefs.current[nextIndex]?.focus();
    //     };

    //     const minValue = row.original.min_reading;
    //     const maxValue = row.original.max_reading;

    //     const isMinMaxValid =
    //       minValue !== "" &&
    //       maxValue !== "" &&
    //       !(Number(minValue) === 0 && Number(maxValue) === 0);

    //     const isOutOfRange =
    //       localValue !== "" &&
    //       isMinMaxValid &&
    //       (Number(localValue) < Number(minValue) ||
    //         Number(localValue) > Number(maxValue));

    //     const handleChange = (e) => {
    //       const value = e.target.value;
    //       setLocalValue(value);
    //       handleValueChange(
    //         value,
    //         row.original.investigation_details.lab_department.department,
    //         row.original.investigation_details.investigation_name,
    //         row.original.observation_id
    //       );
    //     };

    //     const inputType = numericValueAcceptableObservations.includes(
    //       row.original.observation_id
    //     )
    //       ? "number"
    //       : "text";

    //     if (isApproved) {
    //       return (
    //         <span
    //           style={{
    //             // backgroundColor: isOutOfRange ? "#f76060" : "white",
    //             // color: isOutOfRange ? "white" : "black",
    //             // border: isOutOfRange
    //             //   ? "1px solid #f76060"
    //             //   : "1px solid #d9d9d9",
    //             padding: "5px",
    //             // borderRadius: "4px",
    //             // display: "inline-block",
    //           }}
    //         >
    //           {localValue}
    //         </span>
    //       );
    //     }

    //     return isHeader ? (
    //       <span></span>
    //     ) : (
    //       <input
    //         ref={(el) => {
    //           if (el) inputRefs.current.push(el);
    //         }}
    //         type={inputType}
    //         value={localValue}
    //         onChange={handleChange}
    //         onKeyDown={handleKeyDown}
    //         style={{
    //           backgroundColor: isOutOfRange ? "#f76060" : "white",
    //           color: isOutOfRange ? "white" : "black",
    //           border: isOutOfRange ? "1px solid #f76060" : "1px solid #d9d9d9",
    //           padding: "5px",
    //           borderRadius: "4px",
    //           outline: "none",
    //         }}
    //       />
    //     );
    //   },
    // },
    {
      Header: "Value",
      accessor: "observation_value",
      disableSortBy: true,
      Cell: ({ row }) => (
        <ObservationValueCell
          row={row}
          handleValueChange={handleValueChange}
          inputRefs={inputRefs}
          numericValueAcceptableObservations={
            numericValueAcceptableObservations
          }
          calculatedObservationIds={calculatedObservationIds}
          manualOverrideObservationIdsRef={manualOverrideObservationIdsRef}
          unlockedCalculatedObservationIds={
            unlockedCalculatedObservationIds
          }
          handleEnableCalculatedObservationEdit={
            handleEnableCalculatedObservationEdit
          }
          handleResetCalculatedObservation={handleResetCalculatedObservation}
          checkedInvestigations={checkedInvestigations}
          lastFocusedInput={lastFocusedInput}
          editingObservationIdRef={editingObservationIdRef}
          onObservationEditEnd={onObservationEditEnd}
          macInitializedKeysRef={macInitializedKeysRef}
          pendingObservationValuesRef={pendingObservationValuesRef}
          isNavigatingWithinTableRef={isNavigatingWithinTableRef}
          calculationMode={calculationMode}
          handleCalculatedObservationFocus={handleCalculatedObservationFocus}
          readOnlyStatuses={readOnlyStatuses}
        />
      ),
    },
    {
      Header: "Flag",
      accessor: "flag",
      disableSortBy: true,
      Cell: ({ row }) => (
        <ObservationFlagCell
          row={row}
          handleObservationMetaFieldChange={handleObservationMetaFieldChange}
          readOnlyStatuses={readOnlyStatuses}
        />
      ),
    },
    {
      Header: "MacReading",
      accessor: "machine_reading",
      disableSortBy: true,
      Cell: ({ row }) => {
        return row.original.header ? (
          <span></span>
        ) : (
          <span>{row.original.machine_reading}</span>
        );
      },
    },
    {
      Header: "Unit",
      accessor: "unit",
      disableSortBy: true,
      Cell: ({ row }) => {
        return row.original.header ? (
          <span></span>
        ) : (
          <span>{row.original.unit}</span>
        );
      },
    },
    {
      Header: "MinValue",
      accessor: "min_reading",
      disableSortBy: true,
      Cell: ({ row }) => {
        return row.original.header ? (
          <span></span>
        ) : (
          <span>{row.original.min_reading}</span>
        );
      },
    },
    {
      Header: "MaxValue",
      accessor: "max_reading",
      disableSortBy: true,
      Cell: ({ row }) => {
        return row.original.header ? (
          <span></span>
        ) : (
          <span>{row.original.max_reading}</span>
        );
      },
    },
    {
      Header: "Display Reading",
      accessor: "display_reading",
      disableSortBy: true,
      Cell: ({ row }) => (
        <ObservationDisplayReadingCell
          row={row}
          handleObservationMetaFieldChange={handleObservationMetaFieldChange}
          readOnlyStatuses={readOnlyStatuses}
        />
      ),
    },
    {
      Header: "Method",
      accessor: "method_name",
      disableSortBy: true,
      Cell: ({ row }) => {
        return row.original.header ? (
          <span></span>
        ) : (
          <span>{row.original.method_name}</span>
        );
      },
    },
  ];

  return columns;
};

export const INVESTIGATION_OBSERVATION_COLUMNS_DATA = (
  handleValue,
  inputRefs,
  handleToggleObservationNabl,
  nablUpdatingKeys = []
) => {
  const columns = [
    {
      Header: "Observation Name",
      accessor: "observation_obj.observation",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isHeader = row.original.header === true;

        return (
          <>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px" }}
            >
              <span
                style={{
                  fontWeight: isHeader ? "bold" : "normal",
                  textTransform: isHeader ? "uppercase" : "none",
                  marginLeft: isHeader ? "-12px" : "0px",
                }}
              >
                {row.original.observation_obj.observation}
              </span>
            </div>
          </>
        );
      },
    },
    ...(handleToggleObservationNabl
      ? [
          {
            Header: "isNabl",
            accessor: "is_nabl",
            disableSortBy: true,
            Cell: ({ row }) => {
              const isHeader = row.original.header === true;
              const key = `${row.original?.investigation_id}-${row.original?.observation_id}`;

              if (isHeader) {
                return <span></span>;
              }

              return (
                <input
                  type="checkbox"
                  checked={Boolean(row.original?.is_nabl)}
                  disabled={nablUpdatingKeys.includes(key)}
                  onChange={(e) =>
                    handleToggleObservationNabl(row.original, e.target.checked)
                  }
                />
              );
            },
          },
        ]
      : []),
    {
      Header: "Value",
      accessor: "observation_value",
      disableSortBy: true,
      Cell: ({ row }) => (
        <SimpleObservationValueCell row={row} handleValue={handleValue} />
      ),
    },
    {
      Header: "MacReading",
      accessor: "observation_value_mac_reading",
      disableSortBy: true,
      Cell: ({ row }) => (
        <ObservationMacReadingCell row={row} handleValue={handleValue} />
      ),
    },
    {
      Header: "",
      accessor: "ids",
      disableSortBy: true,
      Cell: ({ row }) => {
        return;
        <span></span>;
      },
    },
  ];

  return columns;
};

export const COLUMN_DATA = ({
  temp,
  state,
  setState,
  handleViewModal,
  mergedArray,
  setTempone,
  orgtemp,
  setorgtemp,
}) => {
  const handleInputChange = (row, e) => {
    const value = e.target.value;
    const updatedState = state.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, remarks: value }
        : item
    );
    const updatedOrgTemp = orgtemp.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, remarks: value }
        : item
    );
    setState(updatedState);
    setorgtemp(updatedOrgTemp);
    updateRowInArray({ ...row, remarks: value });
  };

  const handleBarcodeChange = (row, e) => {
    const value = e.target.value;
    const updatedState = state.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, barcode_no: value }
        : item
    );
    const updatedOrgTemp = orgtemp.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, barcode_no: value }
        : item
    );
    setState(updatedState);
    setorgtemp(updatedOrgTemp);
    updateRowInArray({ ...row, barcode_no: value });
  };

  const handleCheckboxChange = (row, e) => {
    const checked = e.target.checked;
    const updatedState = state.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, ifDefault: checked }
        : item
    );
    const updatedOrgTemp = orgtemp.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, ifDefault: checked }
        : item
    );
    setState(updatedState);
    setorgtemp(updatedOrgTemp);
    updateRowInArray({ ...row, ifDefault: checked });
  };

  const handleSampleTypeChange = (row, e) => {
    const value = Number(e.target.value);
    const updatedState = state.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, selectedSampleTypeId: value }
        : item
    );
    const updatedOrgTemp = orgtemp.map((item) =>
      item.investigation_id === row.investigation_id
        ? { ...item, selectedSampleTypeId: value }
        : item
    );
    setState(updatedState);
    setorgtemp(updatedOrgTemp);
    updateRowInArray({ ...row, selectedSampleTypeId: value });
  };

  const updateRowInArray = (row) => {
    const existingRowIndex = temp.findIndex(
      (item) => item.investigation_id === row.investigation_id
    );
    if (existingRowIndex !== -1) {
      temp[existingRowIndex] = { ...temp[existingRowIndex], ...row };
      setTempone([...temp]);
    } else {
      temp.push({ ...row, status: "COLLECTED" });
      setTempone([...temp]);
    }
  };

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  return [
    {
      Header: "Color Code",
      accessor: "container_colour_code",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <div
            style={{
              backgroundColor: cell.value,
              width: "100%",
              height: "40px",
            }}
          />
        );
      },
    },
    {
      Header: "Sample",
      accessor: "sample_type_obj",
      disableSortBy: true,
      Cell: ({ cell }) => {
        const { sample_type_obj, investigation_id } = cell.row.original;
        const selectedSampleTypeId =
          state.find((item) => item.investigation_id === investigation_id)
            ?.selectedSampleTypeId || "";
        const defaultSampleType = sample_type_obj?.find(
          (option) => option.if_default
        );
        const isEmpty = !sample_type_obj || sample_type_obj.length === 0;

        return (
          <select
            style={{
              width: "120px",
              padding: "2px",
              borderRadius: "5px",
              borderColor: isEmpty ? "red" : "#DEE2E6",
              backgroundColor: isEmpty ? "#FFD6D6" : "white",
            }}
            value={
              selectedSampleTypeId ||
              (defaultSampleType ? defaultSampleType.sample_type_id : "")
            }
            onChange={(e) => handleSampleTypeChange(cell.row.original, e)}
            disabled={isEmpty}
          >
            {isEmpty ? (
              <option value="">No Sample Available</option>
            ) : (
              sample_type_obj.map((option, index) => (
                <option key={index} value={option.sample_type_id}>
                  {decodeHtml(option.sample_type)}
                </option>
              ))
            )}
          </select>
        );
      },
    },
    {
      Header: "Barcode",
      accessor: "barcode_no",
      disableSortBy: true,
      Cell: ({ cell }) => {
        const barcodeValue =
          state.find(
            (item) =>
              item.investigation_id === cell.row.original.investigation_id
          )?.barcode_no || "";
        return (
          <input
            type="text"
            defaultValue={barcodeValue || cell.row.original.barcode_no || ""}
            onBlur={(e) => handleBarcodeChange(cell.row.original, e)}
            style={{ width: "120px" }}
          />
        );
      },
    },
    {
      Header: "Investigation",
      accessor: "investigation_name",
      disableSortBy: true,
    },
    {
      Header: "Remarks",
      accessor: "remarks",
      disableSortBy: true,
      Cell: ({ cell }) => {
        const remarksValue =
          state.find(
            (item) =>
              item.investigation_id === cell.row.original.investigation_id
          )?.remarks || "";
        return (
          <input
            type="text"
            defaultValue={remarksValue}
            onBlur={(e) => handleInputChange(cell.row.original, e)}
            style={{ width: "120px" }}
          />
        );
      },
    },
    {
      Header: "View",
      accessor: "view",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleViewModal(cell.row.original, true)}
          >
            View
          </button>
        );
      },
    },
    {
      Header: "Default",
      accessor: "ifDefault",
      disableSortBy: true,
      Cell: ({ cell }) => {
        const isDefault = state.find(
          (item) => item.investigation_id === cell.row.original.investigation_id
        )?.ifDefault;

        return (
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => handleCheckboxChange(cell.row.original, e)}
          />
        );
      },
    },
  ];
};

export const DOCUMENT_FORM_CONFIG = (rowData) => [
  {
    label: "Document Name",
    type: "input-text",
    name: "document_name",
    defaultValue: rowData?.document_name || "",
    validations: {
      maxLength: 255,
      required: true,
    },
  },
];

export const OPTIONS = [{ key: "document_name", value: "Document Name" }];

export const STATUS_OPTIONS = [
  { key: true, value: "Active" },
  { key: false, value: "Inactive" },
];

const today = new Date();
const yyyy = today.getFullYear();
let mm = today.getMonth() + 1;
let dd = today.getDate();

if (dd < 10) dd = "0" + dd;
if (mm < 10) mm = "0" + mm;

const formattedToday = yyyy + "-" + mm + "-" + dd;

today.setDate(today.getDate() - 1);
let yesterdayDd = today.getDate();
let yesterdayMm = today.getMonth() + 1;
const yesterdayYyyy = today.getFullYear();

if (yesterdayDd < 10) yesterdayDd = "0" + yesterdayDd;
if (yesterdayMm < 10) yesterdayMm = "0" + yesterdayMm;

const formattedYesterday =
  yesterdayYyyy + "-" + yesterdayMm + "-" + yesterdayDd;

export const FILTER_DEFINITIONS = [
  {
    name: "keyword",
    type: FILTER_TYPE.SELECT,
    initialValue: OPTIONS[0].key,
  },
  {
    name: "text",
    type: FILTER_TYPE.TEXT,
    initialValue: "",
    debounce: true,
  },
  {
    name: "is_active",
    type: FILTER_TYPE.SELECT,
    initialValue: STATUS_OPTIONS[0].key,
  },
  {
    name: "from_date",
    type: FILTER_TYPE.SELECT,
    initialValue: formattedToday,
  },
  {
    name: "to_date",
    type: FILTER_TYPE.SELECT,
    initialValue: formattedToday,
  },
];
